import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  assessmentDone,
  assessmentPrompt,
  assessmentQuestions,
  emptyShowcase,
  profileUrl,
  slugFor,
  voiceIntroPromptFor,
  type ShowcaseProfile,
} from "@/components/site/showcase";
import { SalesProfileView } from "@/components/site/SalesProfile";
import { emptyProfile, type CandidateProfile } from "@/components/site/candidate";
import { CandidateShell } from "@/components/site/CandidateShell";
import { emptyCvExtract, mergeCvIntoProfile, mergeCvIntoShowcase } from "@/components/site/cv-merge";
import { extractCv } from "@/lib/cv-extract.functions";
import { readCvText } from "@/lib/cv-text";
import { syncProfileIndexing } from "@/lib/profile-seo.functions";
import {
  fetchMyProfile,
  fetchMyShowcase,
  getCandidateSession,
  getCandidateToken,
  updateMyProfile,
  updateMyShowcase,
  uploadFile,
} from "@/lib/candidate-api";

export const Route = createFileRoute("/my-profile")({
  head: () => {
    const t = "Your CLOSDD sales profile | CLOSDD";
    const d =
      "Build your CLOSDD sales profile: a 60-second voice introduction, the CLOSDD sales assessment, your results, skills and what you're looking for next.";

    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "profile" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: MyProfilePage,
});

const roleOptions = [
  "Account executive",
  "Account manager",
  "Appointment setter",
  "Business development manager",
  "Business manager",
  "Customer success",
  "Fundraiser",
  "High-Ticket Closer",
  "Recruitment consultant",
  "Sales executive",
  "Sales manager",
  "SDR or BDR",
];
const yearsOptions = ["No experience", "Less than 1 year", "1 to 2 years", "3 to 5 years", "6 to 10 years", "10+ years"];
const salaryOptions = [
  "Commission only",
  "£20k to £25k",
  "£25k to £30k",
  "£30k to £35k",
  "£35k to £40k",
  "£40k to £45k",
  "£45k to £50k",
  "£50k to £55k",
  "£55k to £60k",
  "£60k+",
];

const inputClass =
  "h-[46px] w-full rounded-[4px] border border-[#D6D3C8] bg-white px-3 text-[15px] text-[#0B2160] outline-none placeholder:text-[#7A7F99] focus:outline-2 focus:outline-offset-1 focus:outline-[#FF5A3C]";
const fieldLabel = "flex flex-col gap-1.5 text-[14px] font-bold text-[#2B3150]";
const card = "rounded-lg border border-[#E3E1D9] bg-white";
const outlineBtn =
  "flex h-[42px] items-center rounded-[4px] border-[1.5px] border-[#0B2160] bg-white px-4 text-[14px] font-extrabold text-[#0B2160]";

/** Keeps a value saved under an older option label selectable instead of silently showing "Select". */
function withCurrent(options: string[], value: string) {
  return value && !options.includes(value) ? [value, ...options] : options;
}

const clock = (n: number) => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;

function SectionHead({ n, title, aside, blurb }: { n: string; title: ReactNode; aside?: string; blurb: string }) {
  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="font-mono text-[12px] text-[#C2381B]">{n}</p>
        {aside ? <span className="text-[13px] font-bold text-[#5E6380]">{aside}</span> : null}
      </div>
      <h2 className="mb-1 mt-1 text-[21px] font-extrabold text-[#0B2160]">{title}</h2>
      <p className="text-[14px] text-[#5E6380]">{blurb}</p>
    </>
  );
}

function MyProfileSkeleton() {
  const bar = "animate-pulse rounded-lg border border-[#E3E1D9] bg-white";
  return (
    <div className="cl min-h-screen bg-[#F4F3EE]" aria-busy="true" aria-label="Loading">
      <div className="h-[72px] bg-[#0B2160]" />
      <div className="h-[54px] border-b border-[#E3E1D9] bg-white" />
      <main className="mx-auto grid w-full max-w-[860px] gap-4 px-6 pb-16 pt-8">
        <div className="h-9 w-72 animate-pulse rounded-[4px] bg-[#E3E1D9]" />
        <div className={`h-32 ${bar}`} />
        <div className={`h-28 ${bar}`} />
        <div className={`h-80 ${bar}`} />
      </main>
    </div>
  );
}

function MyProfilePage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [account, setAccount] = useState<CandidateProfile>(emptyProfile);
  const [p, setP] = useState<ShowcaseProfile>(emptyShowcase);
  const [sector, setSector] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!getCandidateToken()) {
      navigate({ to: "/login" });
      return;
    }
    // Requested alongside the session check rather than after it, so the
    // skeleton waits on one round trip instead of two.
    const data = Promise.all([fetchMyProfile(), fetchMyShowcase()]);
    data.catch(() => undefined);
    getCandidateSession().then((user) => {
      if (cancelled) return;
      if (!user) {
        navigate({ to: "/login" });
        return;
      }
      data
        .then(([profile, showcase]) => {
          if (cancelled) return;
          setAccount(profile);
          setSector(profile.sector ?? "");
          setP(showcase);
          setReady(true);
          setLoadError("");
        })
        .catch((err) => {
          // The session is already confirmed valid above — a failed
          // profile/showcase fetch here is transient, not a logged-out state.
          if (!cancelled) {
            setLoadError(err instanceof Error ? err.message : "We couldn't load your profile. Try again.");
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, retryCount]);

  const set = <K extends keyof ShowcaseProfile>(k: K, v: ShowcaseProfile[K]) => {
    setP((prev) => ({ ...prev, [k]: v }));
    setSaved(false);
  };

  const save = async (attempt = 0): Promise<boolean> => {
    setSaveError("");
    setSaving(true);
    // Clean, permanent slug (name based) — no internal ids in the URL. Keep
    // the existing slug on resave; only mint a new one the first time.
    const slug = p.slug || slugFor(account.name || p.headline || "salesperson");
    try {
      const next = await updateMyShowcase({ ...p, slug, enabled: true });
      setP(next);
      if (sector !== (account.sector ?? "")) setAccount(await updateMyProfile({ ...account, sector }));
      setSaved(true);
      // Keeps the XML sitemap in sync: public profiles in, private ones out.
      void syncProfileIndexing({ data: { slug, isPublic: next.visibility === "public" } }).catch(() => {});
      return true;
    } catch (err) {
      // Slug collision (rare — slugFor already appends a random suffix):
      // mint a fresh one and retry once.
      if (!p.slug && attempt === 0) {
        set("slug", "");
        return save(1);
      }
      setSaveError(err instanceof Error ? err.message : "We couldn't save your profile.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="cl grid min-h-screen place-items-center bg-[#F4F3EE] px-6">
        <div className="grid max-w-[420px] gap-4 text-center">
          <p className="text-[17px] text-[#3A3A40]">{loadError}</p>
          <button
            type="button"
            onClick={() => setRetryCount((n) => n + 1)}
            className="mx-auto min-h-11 rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!ready) return <MyProfileSkeleton />;

  const slug = p.slug || slugFor(account.name || "salesperson", account.email);
  const link = profileUrl(slug);
  const answered = assessmentDone(p);
  const complete = Boolean(p.audioUrl) && answered >= assessmentQuestions.length;
  const approved = Boolean(account.approved);
  const submitted = complete && (saved || Boolean(p.savedAt));
  const status = approved
    ? { label: "Live", cls: "bg-[#E2F3E9] text-[#0B6B40]" }
    : submitted
      ? { label: "Waiting for Closdd approval", cls: "bg-[#FFE9A8] text-[#6B4A00]" }
      : complete
        ? { label: "Ready to send for approval", cls: "bg-[#E2F3E9] text-[#0B6B40]" }
        : { label: "Not live yet", cls: "bg-[#FFF1C7] text-[#7A5200]" };
  const saveHint = saved
    ? complete
      ? "✓ Saved. Closdd will check your profile and put it live, usually within 24 hours."
      : "✓ Profile saved"
    : complete
      ? "Everything is done. Save to send it to Closdd for approval."
      : "Record your pitch and all 5 answers, then Closdd approves it to go live.";
  const roles = p.targetRoles ?? [];

  const copy = () => {
    void navigator.clipboard?.writeText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  return (
    <CandidateShell page="profile" name={account.name} email={account.email}>
      <main className="mx-auto flex w-full max-w-[860px] flex-col gap-4 px-6 pb-16 pt-8">
        <div className="mb-1 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-[32px] font-extrabold tracking-[-1px] text-[#0B2160]">Your sales profile</h1>
              <span className={`rounded-full px-2.5 py-[3px] text-[12px] font-bold ${status.cls}`}>{status.label}</span>
            </div>
            <p className="mt-1.5 max-w-[560px] text-[15px] leading-[1.5] text-[#5E6380]">
              Everything employers see: your CV, the basics, your pitch and your assessment. Change anything any time; employers always see the latest version.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMode(mode === "edit" ? "preview" : "edit")}
            className="flex h-[42px] items-center rounded-[4px] border-[1.5px] border-[#D6D3C8] bg-white px-3.5 text-[14px] font-bold text-[#0B2160]"
          >
            {mode === "edit" ? "Employer view" : "← Back to editing"}
          </button>
        </div>

        {mode === "preview" ? (
          <div className="mt-2">
            <SalesProfileView name={account.name} p={p} email={account.email} phone={account.phone} />
          </div>
        ) : (
          <>
            <nav aria-label="Profile sections" className="flex flex-wrap gap-1.5">
              {[
                ["cv", "1 · CV"],
                ["basics", "2 · The basics"],
                ["pitch", "3 · 60-second pitch"],
                ["assessment", "4 · Sales assessment"],
                ["video", "5 · Video (optional)"],
              ].map(([id, label]) => (
                <a key={id} href={`#${id}`} className="flex h-[34px] items-center rounded-full border border-[#D6D3C8] bg-white px-3 text-[13px] font-bold text-[#0B2160]">
                  {label}
                </a>
              ))}
            </nav>

            <section aria-labelledby="link-h" className={`${card} px-6 py-5`}>
              <h2 id="link-h" className="text-[17px] font-extrabold text-[#0B2160]">Your public profile link</h2>
              <p className="mb-3 mt-1 text-[14px] leading-[1.5] text-[#5E6380]">
                Add it to your CV header, LinkedIn featured section, email signature or anywhere employers can find you.
              </p>
              {!approved ? <p className="-mt-1 mb-3 text-[13px] font-bold text-[#7A5200]">Employers can open it once Closdd puts your profile live.</p> : null}
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex h-[42px] min-w-0 flex-[1_1_280px] items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[4px] border border-[#E3E1D9] bg-[#FAF9F5] px-3 font-mono text-[13px] text-[#0B2160]">
                  {link}
                </span>
                <a href={link} target="_blank" rel="noopener" className={`${outlineBtn} px-4`}>
                  View
                </a>
                <button type="button" onClick={copy} className="h-[42px] rounded-[4px] bg-[#0B2160] px-4 text-[14px] font-extrabold text-white">
                  {copied ? "✓ Copied" : "Copy link"}
                </button>
              </div>
            </section>

            <CvSection
              profile={account}
              onUpdated={(profile, showcase) => {
                setAccount(profile);
                setSector(profile.sector ?? "");
                if (showcase) setP((prev) => ({ ...prev, ...showcase }));
              }}
            />

            <section id="basics" aria-labelledby="s1" className={`${card} scroll-mt-4 p-6`}>
              <SectionHead n="02" title="The basics" blurb="The first thing an employer reads. Be specific about what you sell and who to." />
              <div className="mt-[18px] grid items-end gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
                <fieldset className="min-w-0 [grid-column:1/-1]">
                  <legend className="text-[14px] font-bold text-[#2B3150]">
                    Roles you want <span className="font-medium text-[#5E6380]">· choose up to 3 · {roles.length} of 3 chosen</span>
                  </legend>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {roleOptions.map((r) => {
                      const on = roles.includes(r);
                      const locked = !on && roles.length >= 3;
                      return (
                        <button
                          key={r}
                          type="button"
                          aria-pressed={on}
                          aria-disabled={locked}
                          onClick={() => !locked && set("targetRoles", on ? roles.filter((x) => x !== r) : [...roles, r])}
                          className={`h-[38px] rounded-full border-[1.5px] px-3.5 text-[14px] font-bold ${
                            on
                              ? "border-[#0B2160] bg-[#0B2160] text-white"
                              : locked
                                ? "cursor-not-allowed border-[#E3E1D9] bg-[#F4F3EE] text-[#A3A6B8]"
                                : "border-[#D6D3C8] bg-white text-[#0B2160]"
                          }`}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                <label className={fieldLabel}>
                  Location
                  <input value={p.location} onChange={(e) => set("location", e.target.value)} placeholder="London, United Kingdom" className={inputClass} />
                </label>
              </div>
              <label className={`${fieldLabel} mt-4`}>
                Headline
                <input value={p.headline} onChange={(e) => set("headline", e.target.value)} className={inputClass} />
              </label>
              <div className="mt-4 grid items-end gap-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
                <label className={fieldLabel}>
                  What you sell best
                  <input
                    value={sector}
                    onChange={(e) => {
                      setSector(e.target.value);
                      setSaved(false);
                    }}
                    placeholder="e.g. B2B SaaS"
                    className={inputClass}
                  />
                </label>
                <label className={fieldLabel}>
                  Industries
                  <input value={p.industries} onChange={(e) => set("industries", e.target.value)} placeholder="SaaS, Technology, B2B" className={inputClass} />
                </label>
                <label className={fieldLabel}>
                  <span>
                    LinkedIn <span className="font-medium text-[#5E6380]">(optional)</span>
                  </span>
                  <input type="url" value={p.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="linkedin.com/in/you" className={inputClass} />
                </label>
                <label className={fieldLabel}>
                  Years selling
                  <select value={p.yearsSelling} onChange={(e) => set("yearsSelling", e.target.value)} className={`${inputClass} px-2.5`}>
                    <option value="">Select</option>
                    {withCurrent(yearsOptions, p.yearsSelling).map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </label>
                <label className={fieldLabel}>
                  Salary expectation
                  <select value={p.salaryExpectation} onChange={(e) => set("salaryExpectation", e.target.value)} className={`${inputClass} px-2.5`}>
                    <option value="">Select a range</option>
                    {withCurrent(salaryOptions, p.salaryExpectation).map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="mt-2 text-[13px] text-[#5E6380]">Pick the range you'd accept. Every band is a £5k window you can negotiate inside.</p>
              <label className={`${fieldLabel} mt-4`}>
                About you
                <textarea
                  rows={6}
                  value={p.bio}
                  onChange={(e) => set("bio", e.target.value)}
                  className="w-full resize-y rounded-[4px] border border-[#D6D3C8] bg-white p-3 text-[15px] font-normal leading-[1.5] text-[#0B2160] outline-none focus:outline-2 focus:outline-offset-1 focus:outline-[#FF5A3C]"
                />
              </label>
            </section>

            <section id="pitch" aria-labelledby="s2" className={`${card} scroll-mt-4 p-6`}>
              <SectionHead
                n="03"
                aside="Max 60 seconds"
                title="60-second pitch"
                blurb="Your 60-second pitch, and the most powerful part of your profile. Employers hear it first, and it's never locked."
              />
              <div className="mt-4 rounded-md bg-[#F4F3EE] px-[18px] py-4 text-[15px] leading-[1.55] text-[#2B3150]">{voiceIntroPromptFor(p)}</div>
              <Recorder
                idleLabel="Record pitch"
                url={p.audioUrl}
                seconds={p.audioSeconds}
                onUrl={(u, seconds) => {
                  set("audioUrl", u);
                  set("audioSeconds", u ? seconds : 0);
                }}
              />
              <p className="mt-2 text-[13px] text-[#5E6380]">Maximum 60 seconds. Re-record as many times as you like before you publish.</p>
            </section>

            <section id="assessment" aria-labelledby="s3" className={`${card} scroll-mt-4 overflow-hidden`}>
              <div className="px-6 pb-[18px] pt-6">
                <SectionHead
                  n="04"
                  aside={`${answered} of ${assessmentQuestions.length} answered`}
                  title="Closdd sales assessment"
                  blurb="Five questions, answered out loud. Employers hear how you think, communicate and handle real sales situations."
                />
              </div>
              <ol>
                {assessmentQuestions.map((q, i) => {
                  const take = p.assessment[q.id];
                  const done = Boolean(take?.url);
                  return (
                    <li key={q.id} className="flex flex-wrap items-start gap-4 border-t border-[#ECEAE3] px-6 py-[18px]">
                      <span
                        className={`grid h-8 w-8 flex-none place-items-center rounded-full text-[14px] font-extrabold ${
                          done ? "bg-[#E2F3E9] text-[#0B6B40]" : "bg-[#ECEAE3] text-[#0B2160]"
                        }`}
                      >
                        {done ? "✓" : i + 1}
                      </span>
                      <div className="min-w-0 flex-[1_1_320px]">
                        <p className="text-[13px] font-extrabold text-[#0B2160]">
                          {q.title} <span className="font-medium text-[#5E6380]">({q.guide})</span>
                        </p>
                        <p className="mt-1 text-[15px] leading-[1.5] text-[#2B3150]">{assessmentPrompt(take, q)}</p>
                        <Recorder
                          small
                          idleLabel="Record answer"
                          url={take?.url ?? ""}
                          seconds={take?.seconds ?? 0}
                          onUrl={(u, seconds) => {
                            const next = { ...p.assessment };
                            // Keep any custom wording an admin set for this question.
                            const question = take?.question;
                            if (u) next[q.id] = { url: u, seconds, recordedAt: new Date().toISOString(), ...(question ? { question } : {}) };
                            else if (question) next[q.id] = { url: "", seconds: 0, recordedAt: "", question };
                            else delete next[q.id];
                            set("assessment", next);
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>

            <section id="video" aria-labelledby="s4" className={`${card} scroll-mt-4 p-6`}>
              <SectionHead
                n="05"
                title={
                  <>
                    Video introduction <span className="text-[15px] font-medium text-[#5E6380]">(optional)</span>
                  </>
                }
                blurb="Want to make an even better first impression? Add a short video introduction."
              />
              <VideoField url={p.videoUrl} onUrl={(u) => set("videoUrl", u)} />
            </section>

            <div className={`${card} flex flex-wrap items-center gap-4 px-6 py-[18px]`}>
              <span className={`min-w-0 flex-[1_1_240px] text-[14px] ${saveError ? "font-bold text-[#A62B12]" : saved ? "font-bold text-[#0B6B40]" : "text-[#4A4F6A]"}`}>
                {saveError || saveHint}
              </span>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  if (await save()) setMode("preview");
                }}
                className="flex h-12 items-center rounded-[4px] border-[1.5px] border-[#0B2160] px-[18px] text-[15px] font-extrabold text-[#0B2160] disabled:opacity-60"
              >
                Save and view as employer
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void save()}
                className="h-12 rounded-[4px] bg-[#0B2160] px-6 text-[16px] font-extrabold text-white disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save profile"}
              </button>
            </div>
          </>
        )}

        <p className="mt-2 text-[14px] text-[#5E6380]">
          Need help?{" "}
          <a href="mailto:info@closdd.com" className="font-bold text-[#0B2160] underline underline-offset-2">
            info@closdd.com
          </a>
        </p>
      </main>
    </CandidateShell>
  );
}

/** 01 · CV — replacing it re-reads the file and fills in anything still empty. */
function CvSection({
  profile,
  onUpdated,
}: {
  profile: CandidateProfile;
  onUpdated: (p: CandidateProfile, showcase?: Partial<ShowcaseProfile>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const runExtract = useServerFn(extractCv);

  async function handle(file: File) {
    setBusy(true);
    setNote("");
    setError("");
    try {
      const cvUrl = await uploadFile(file, file.name);
      // AI auto-fill is best-effort: the file is already safely stored above,
      // so a read/extraction failure should not lose that.
      let cv = emptyCvExtract;
      let autoFilled = true;
      try {
        const text = await readCvText(file);
        if (text.length < 30) throw new Error("no readable text");
        cv = await runExtract({ data: { text } });
      } catch {
        autoFilled = false;
      }
      const merged = mergeCvIntoProfile(cv, file.name, cvUrl, { ...profile, cvName: file.name, cvUrl });
      const saved = await updateMyProfile(merged);
      let showcase: ShowcaseProfile | undefined;
      if (autoFilled) showcase = await updateMyShowcase(mergeCvIntoShowcase(cv, await fetchMyShowcase()));
      onUpdated({ ...merged, ...saved }, showcase);
      setNote(autoFilled ? "CV read. Anything still empty below has been filled in from it." : "CV uploaded. We couldn't read details from it this time — fill them in below.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't upload that CV. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="cv" aria-labelledby="s0" className={`${card} scroll-mt-4 p-6`}>
      <SectionHead n="01" title="Your CV" blurb="Upload your CV and Closdd fills in the basics below for you. PDF works best, max 10MB." />
      <div className="mt-3.5 flex flex-wrap items-center gap-3">
        <span className="inline-flex min-w-0 flex-[1_1_260px] items-center gap-1.5 break-all rounded-[4px] bg-[#F4F3EE] px-3 py-2.5 text-[14px] text-[#2B3150]">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5" />
          </svg>
          {profile.cvName || "No CV uploaded yet"}
        </span>
        <label className={`${outlineBtn} relative cursor-pointer ${busy ? "opacity-60" : ""}`}>
          {busy ? "Reading your CV…" : profile.cvName ? "Replace CV and auto-fill" : "Upload CV and auto-fill"}
          <input
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            disabled={busy}
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void handle(f);
            }}
          />
        </label>
      </div>
      {note ? <p className="mt-3 text-[14px] font-bold text-[#0B6B40]">{note}</p> : null}
      {error ? <p className="mt-3 text-[14px] font-bold text-[#A62B12]">{error}</p> : null}
    </section>
  );
}

function VideoField({ url, onUrl }: { url: string; onUrl: (u: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const linked = url.startsWith("blob:") ? "" : url;

  return (
    <>
      <div className="mt-[18px] flex flex-wrap items-end gap-3">
        <label className={`${fieldLabel} min-w-0 flex-[1_1_320px]`}>
          Paste a video link
          <input type="url" value={linked} onChange={(e) => onUrl(e.target.value)} placeholder="https://www.loom.com/share/…" className={inputClass} />
        </label>
        <span className="flex h-[46px] items-center text-[14px] text-[#5E6380]">or</span>
        <label className={`${outlineBtn} relative h-[46px] cursor-pointer ${uploading ? "opacity-60" : ""}`}>
          {uploading ? "Uploading…" : fileName ? `✓ ${fileName}` : "Upload a video"}
          <input
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            disabled={uploading}
            className="sr-only"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setError(null);
              setUploading(true);
              try {
                onUrl(await uploadFile(f, f.name));
                setFileName(f.name);
              } catch (err) {
                setError(err instanceof Error ? err.message : "Could not upload that video. Try again.");
              } finally {
                setUploading(false);
              }
            }}
          />
        </label>
      </div>
      <p className="mt-2 text-[13px] text-[#5E6380]">MP4 or MOV. 60 to 90 seconds is plenty.</p>
      {error ? <p className="mt-2 text-[13px] font-bold text-[#A62B12]">{error}</p> : null}
      {url && !url.startsWith("blob:") && /\.(mp4|mov|webm)(\?|$)/i.test(url) ? (
        <video src={url} controls className="mt-4 w-full rounded-md border border-[#E3E1D9] bg-black" />
      ) : null}
    </>
  );
}

function extForMime(mime: string): string {
  const m = mime.split(";")[0]?.trim() ?? "";
  if (m.includes("webm")) return "webm";
  if (m.includes("ogg")) return "ogg";
  if (m.includes("mp4")) return "mp4";
  if (m.includes("wav")) return "wav";
  if (m.includes("mpeg")) return "mp3";
  return "webm";
}

/** Record / Stop / Re-record control with its status line, as in the design; uploads the take when stopped. */
function Recorder({
  url,
  seconds: savedSeconds,
  idleLabel,
  small,
  onUrl,
}: {
  url: string;
  seconds: number;
  idleLabel: string;
  small?: boolean;
  onUrl: (u: string, seconds: number) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countRef = useRef(0);

  const stop = () => {
    recorderRef.current?.stop();
    recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
  };

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        const mimeType = rec.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type: mimeType });
        const takenSeconds = countRef.current;
        setUploading(true);
        try {
          onUrl(await uploadFile(blob, `recording.${extForMime(mimeType)}`), takenSeconds);
        } catch (err) {
          setError(err instanceof Error ? err.message : "Could not save that recording. Try again.");
        } finally {
          setUploading(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      countRef.current = 0;
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        countRef.current += 1;
        setSeconds(countRef.current);
      }, 1000);
    } catch {
      setError("We couldn't reach your microphone. Check browser permissions and try again.");
    }
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    [],
  );

  const state = recording ? "rec" : url ? "done" : "none";
  const btn =
    state === "rec"
      ? "border-[#C2381B] bg-white text-[#A82E12]"
      : state === "done"
        ? "border-[#0B2160] bg-white text-[#0B2160]"
        : "border-[#0B2160] bg-[#0B2160] text-white";
  const dot = state === "rec" ? "bg-[#C2381B]" : state === "done" ? "bg-[#D6D3C8]" : "bg-[#FF5A3C]";

  return (
    <div className={small ? "mt-3" : "mt-4"}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={recording ? stop : start}
          disabled={uploading}
          className={`flex items-center gap-2 rounded-[4px] border-[1.5px] font-extrabold disabled:opacity-60 ${btn} ${small ? "h-10 px-4 text-[14px]" : "h-11 px-[18px] text-[15px]"}`}
        >
          <span aria-hidden className={`rounded-full ${dot} ${small ? "h-[9px] w-[9px]" : "h-2.5 w-2.5"}`} />
          {uploading ? "Saving…" : state === "rec" ? "Stop recording" : state === "done" ? "Re-record" : idleLabel}
        </button>
        <span className={`text-[14px] font-bold ${state === "rec" ? "text-[#A82E12]" : state === "done" ? "text-[#0B6B40]" : "text-[#5E6380]"}`}>
          {state === "rec"
            ? `Recording… ${clock(seconds)}`
            : state === "done"
              ? `✓ Recorded${savedSeconds ? ` · ${clock(savedSeconds)}` : ""}`
              : "Not recorded yet"}
        </span>
        {state === "done" ? (
          <button type="button" onClick={() => onUrl("", 0)} className="text-[13px] font-bold text-[#4A4F6A] underline underline-offset-[3px]">
            Delete
          </button>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-[13px] font-bold text-[#A62B12]">{error}</p> : null}
      {url && !recording ? <audio src={url} controls className="mt-3 w-full max-w-[420px]" /> : null}
    </div>
  );
}
